import type { AgentDriver } from "@skaile/workspaces/bridge";
import { classifyClaudeSdkError } from "@skaile/workspaces/bridge";
import type { CompactionConfig } from "@skaile/workspaces/core";
import { COMPACTION_DEFAULTS } from "@skaile/workspaces/core";
import type {
  CompactionAttemptEvent,
  CompactionTrigger,
  SnapshotEvent,
  TokenUsage,
} from "@skaile/workspaces/types";
import { buildCompactionPrompt } from "./prompt.js";
import { type CompactionStrategy, TokenThresholdStrategy } from "./strategy.js";

/**
 * Fields shared across every {@link CompactionAttemptEvent} emitted within a
 * single `_doCompact` call — captured once at the start so all three branches
 * (thrown failure / validation failure / success) agree on the same values.
 * Each branch supplies the remaining status-specific fields.
 */
type CompactionAttemptBase = Omit<
  CompactionAttemptEvent,
  "type" | "status" | "errorCode" | "errorMessage" | "summary" | "outputTokens" | "validatedAt"
>;

/**
 * Minimum character length the LLM must produce to count as a meaningful summary.
 * Below this we mark the attempt `failed`/`too_short`. Tunable: 200 chars is roughly
 * 50 tokens, the floor for "the model said anything substantive".
 */
const MIN_SUMMARY_CHARS = 200;

/**
 * Maximum compression ratio (outputTokens / tokensBefore) we accept as a successful
 * compaction. Above this the LLM essentially returned the input back; mark `poor_ratio`.
 */
const MAX_COMPRESSION_RATIO = 0.9;

/**
 * Context window assumed when the driver reports none. A floor, not a guess at
 * the real model limit: without it a missing window silently disables
 * compaction entirely, which is strictly worse than compacting a little early.
 */
const DEFAULT_CONTEXT_WINDOW = 200_000;

/**
 * Window assumed for a model published under a 1M-context alias. Anthropic's
 * 1M variants are addressed by suffixing the base model id, and the SDK keeps
 * reporting the base 200 000 window for them.
 */
const ONE_MILLION_CONTEXT_WINDOW = 1_000_000;

/**
 * Matches the 1M-context alias suffix on a model id — `claude-opus-5[1m]`, and
 * the `-1m` spelling some catalogues use. The `-1m` form is anchored because
 * unanchored it would match any id with `1m` in the middle.
 */
const ONE_MILLION_ALIAS_PATTERN = /\[1m\]|-1m$/i;

/**
 * Bound on a single compaction `prompt()` call. Sits well under the driver's
 * 30-minute turn-stall ceiling (`SKAILE_TURN_STALL_MS`), which was otherwise
 * the only exit and reports an opaque `unknown`. See the runner CLAUDE.md,
 * including why this is not expected to bite the larger prompt the 1M floor
 * below implies.
 */
const DEFAULT_COMPACTION_TIMEOUT_MS = 10 * 60_000;

/**
 * Resolved per call, never at module load: the runner's env plumbing (and
 * tests) set `SKAILE_COMPACTION_TIMEOUT_MS` after this module is imported.
 */
function compactionTimeoutMs(): number {
  const raw = Number(process.env.SKAILE_COMPACTION_TIMEOUT_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_COMPACTION_TIMEOUT_MS;
}

/**
 * Error codes worth one more try. A `timeout` or an unclassified failure says
 * nothing about the request itself, unlike `rate_limit` / `auth` / `too_short`,
 * where an immediate repeat would fail the same way.
 */
const RETRYABLE_ERROR_CODES = new Set(["timeout", "unknown"]);

/**
 * Focus appended to the single retry after a compaction turn timed out or
 * failed without a classification. It asks for tighter prose, never for less
 * coverage: a re-compaction's reply REPLACES the previous summary, so a retry
 * that told the model to answer with only the newest work would discard it.
 */
const RETRY_FOCUS_DIRECTIVE = `The previous attempt to write this summary did not finish in time, most likely
because it ran long. Be economical: compress aggressively, and drop low-value
detail rather than exceeding a few hundred words.

Cover exactly the same ground as before -- if a previous summary appears above,
still restate everything in it that still matters. The saving comes from
tighter prose, never from leaving material out.`;

/**
 * How long to wait for an aborted compaction turn to actually unwind before
 * retrying. Only the claude-sdk driver's `abort()` waits for its query; the
 * codex and subprocess drivers return first and reject the next `prompt()` as
 * "turn already in progress". Sized just above that driver's own 3s grace.
 */
export const ABORT_SETTLE_GRACE_MS = 5_000;

/**
 * Cap on a previous summary reproduced into a re-compaction prompt. Several
 * times any real summary, so it never bites in practice — `MAX_COMPRESSION_RATIO`
 * bounds a summary only against the context, which is far too loose to keep
 * the next prompt small. Truncation is a last resort and is logged.
 */
const MAX_CARRIED_SUMMARY_CHARS = 24_000;

/**
 * Cap on diagnostic text persisted on a failed attempt. A few KB identifies a
 * failure mode without bloating the mirrored column; the text is never restored
 * into a session, because restoration requires a validated `success` attempt.
 */
const MAX_FAILURE_DIAGNOSTIC_CHARS = 4_000;

function truncateTo(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}... [truncated]`;
}

function truncateDiagnostic(text: string): string {
  return truncateTo(text, MAX_FAILURE_DIAGNOSTIC_CHARS);
}

/**
 * Maximum length under which a captured response is still considered a candidate
 * for being a rate-limit error message rather than a real summary. Real
 * compactions are typically thousands of characters; rate-limit relays are
 * short.
 */
const RATE_LIMIT_TEXT_MAX_CHARS = 1500;

/**
 * Phrases that strongly indicate the captured text is the LLM relaying a
 * rate-limit / quota error to the user rather than producing a summary.
 * Conservative on purpose — a real summary that mentions rate limits in
 * passing would only match if it is also short, see
 * {@link detectRateLimitInSummary}.
 */
const RATE_LIMIT_TEXT_PATTERNS: RegExp[] = [
  /you('?| ha)ve hit your (rate |usage )?limit/i,
  /you('?| ha)ve reached your (usage |rate )?limit/i,
  /you('?| ha)ve been rate[\s-]?limited/i,
  /rate limit (exceeded|reached|hit)/i,
  /usage limit (exceeded|reached)/i,
  /quota (exceeded|reached)/i,
  /claude (ai )?usage limit reached/i,
];

/**
 * Prompt tokens actually occupying the context window on the last turn.
 *
 * Anthropic reports fresh input separately from cache reads/writes: on a cached
 * conversation `inputTokens` is ~2-10 while the real context sits in
 * `cacheReadTokens`, which is why reading `inputTokens` alone made the compaction
 * threshold unreachable. OpenAI's `inputTokens` already *includes* cache reads, so
 * `cacheReadTokens` is a subset there — taking the max of the two rather than the
 * sum keeps that driver from double-counting.
 */
export function contextTokens(usage: TokenUsage | null | undefined): number {
  const input = usage?.inputTokens ?? 0;
  const cacheRead = usage?.cacheReadTokens ?? 0;
  const cacheCreation = usage?.cacheCreationTokens ?? 0;
  return Math.max(input, cacheRead) + cacheCreation;
}

/**
 * Context window the fill is measured against.
 *
 * A 1M alias is floored at 1M: the SDK reports the base 200k window for it, so
 * those sessions compacted far too early. A floor, never an override — the
 * non-alias branch is unchanged, and a nonsensical `0` must not disable it.
 */
export function resolveContextWindow(driverWindow: number | null, model: string | null): number {
  if (model && ONE_MILLION_ALIAS_PATTERN.test(model)) {
    return Math.max(driverWindow ?? 0, ONE_MILLION_CONTEXT_WINDOW);
  }
  return driverWindow || DEFAULT_CONTEXT_WINDOW;
}

/**
 * Detect whether the captured response from a compaction `prompt()` call is
 * actually a rate-limit error message returned as text (instead of as a
 * thrown error). Persisting such a "summary" would corrupt context
 * restoration on the next wake — the snapshot prompt would inject the error
 * text as if it were the conversation summary.
 *
 * Conservative: requires both a strong signal phrase AND a short response.
 * Real summaries are routinely 2k+ chars; rate-limit relays are short.
 *
 * Issue: I-29.
 */
export function detectRateLimitInSummary(text: string): boolean {
  if (text.length > RATE_LIMIT_TEXT_MAX_CHARS) return false;
  return RATE_LIMIT_TEXT_PATTERNS.some((p) => p.test(text));
}

/**
 * Validation of a compaction output's structural shape, run BEFORE we persist
 * it as a `success`. Cheap synchronous checks only — no LLM calls.
 *
 * Failed validation does NOT throw; it returns a structured verdict so the
 * orchestrator can record a `failed` Compaction row with the right errorCode.
 *
 * @param text         The captured summary text from the driver.
 * @param tokensBefore Estimated context size before the call — the whole prompt, cached
 *                     tokens included (from {@link contextTokens}), not `usage.inputTokens`.
 * @param tokensAfter  Output token count of the summary (from driver or chars/4 heuristic).
 * @returns `{ ok: true }` on pass, otherwise `{ ok: false, errorCode }` with one of:
 *          `"empty"` | `"too_short"` | `"poor_ratio"` | `"rate_limit"`.
 *
 * Spec: `_devlog/specs/2026-05-05-session-resume-restart-design.md` § Validation at write time.
 * @docLink packages/runner/capabilities#validate-compaction-output
 */
export function validateCompactionOutput(
  text: string,
  tokensBefore: number,
  tokensAfter: number,
): { ok: true } | { ok: false; errorCode: string } {
  if (!text.trim()) return { ok: false, errorCode: "empty" };
  if (detectRateLimitInSummary(text)) return { ok: false, errorCode: "rate_limit" };
  if (text.length < MIN_SUMMARY_CHARS) return { ok: false, errorCode: "too_short" };
  if (tokensBefore > 0 && tokensAfter / tokensBefore > MAX_COMPRESSION_RATIO) {
    return { ok: false, errorCode: "poor_ratio" };
  }
  return { ok: true };
}

/**
 * Options for the {@link CompactionOrchestrator}.
 *
 * @docLink packages/runner/dev-guide#compaction
 */
export interface CompactionOrchestratorOptions {
  driver: AgentDriver;
  config?: CompactionConfig;
  skillDirectives?: string[];
  mcpDirectives?: string[];
  agentCompactionPrompt?: string;
  onCompactionStart?: (trigger: CompactionTrigger) => void;
  onSnapshot?: (snapshot: SnapshotEvent) => void;
  /**
   * Emitted on EVERY compaction attempt, success or failure. Phase 1 sink for
   * the new `Compaction` table observability surface; the host's serve.ts wires
   * this to forward events on the agent transport.
   */
  onCompactionAttempt?: (event: CompactionAttemptEvent) => void;
  onCompactionEnd?: () => void;
  /**
   * Fires once the compaction call has fully settled and the driver is free.
   *
   * Distinct from {@link onCompactionEnd}, which fires earlier on the success
   * path — before `resetSession()` and the snapshot injection — so that the
   * injection turn's events are not suppressed. A host holding user turns off
   * the driver has to wait for this one instead, or it releases them straight
   * into the injection.
   *
   * Guaranteed on every exit, including a throw.
   */
  onCompactionSettled?: () => void;
  onLog?: (line: string) => void;
  /**
   * Phase 3 (resume cascade): supplies the current capability registry
   * signature at compaction time so it lands on the
   * {@link CompactionAttemptEvent}. The wake selector compares this against
   * the runner's current signature to decide tier-1 native SDK resume
   * eligibility.
   *
   * Returns `null` when the host cannot compute a signature (legacy boot
   * path, registry not yet bootstrapped); the orchestrator records `null`
   * in that case and the resulting compaction is tier-1 ineligible.
   */
  getCurrentCapabilitySignature?: () => string | null;
}

/**
 * Orchestrates context window compaction for a running agent session.
 *
 * Monitors token usage against the configured thresholds and triggers
 * compaction via snapshots when the strategy fires.
 *
 * @docLink packages/runner/dev-guide#compaction
 */
export class CompactionOrchestrator {
  private readonly driver: AgentDriver;
  private readonly strategy: CompactionStrategy;
  private readonly config: Required<CompactionConfig>;
  private readonly opts: CompactionOrchestratorOptions;

  private _isCompacting = false;
  /** Whether {@link CompactionOrchestratorOptions.onCompactionStart} has fired for the current call. */
  private _startFired = false;
  /** Whether {@link CompactionOrchestratorOptions.onCompactionEnd} has fired for the current call. */
  private _endFired = false;
  private lastCompactionSeq: number | null = null;
  private lastCompactionTime: number | null = null;
  /**
   * Summary of the last successful compaction, carried into the next one.
   * Per-instance, in step with `lastCompactionSeq`: a driver rebuild resets
   * both, so the first compaction after one is not treated as a re-compaction.
   */
  private lastSummary: string | null = null;
  private currentSeq = 0;
  private messagesSinceCompaction = 0;

  constructor(opts: CompactionOrchestratorOptions) {
    this.driver = opts.driver;
    this.opts = opts;
    this.config = { ...COMPACTION_DEFAULTS, ...opts.config };
    this.strategy = new TokenThresholdStrategy({
      thresholdPercent: this.config.thresholdPercent,
      minCooldownMs: this.config.minCooldownMs,
    });
  }

  /**
   * How full the context window is. Prefers the driver's per-call figure; the
   * turn-cumulative `getTokenUsage()` overstates it by 10-40x on a multi-tool
   * turn, so it is only the fallback for drivers that report no per-call size.
   * The fallback cannot resurrect that overstatement: a driver clears both
   * readings together, so a null per-call figure implies no cumulative one.
   * `?.` guards duck-typed hosts that predate the accessor, not `AgentDriver`.
   */
  private contextFill(usage: TokenUsage | null): number {
    return this.driver.getContextTokens?.() ?? contextTokens(usage);
  }

  trackMessage(seq: number): void {
    this.currentSeq = seq;
    this.messagesSinceCompaction++;
  }

  shouldCompact(): boolean {
    if (!this.config.enabled) return false;
    const usage = this.driver.getTokenUsage();
    const contextWindow = resolveContextWindow(
      this.driver.getContextWindow(),
      this.driver.getModel() ?? null,
    );
    const estimatedTokens = this.contextFill(usage);
    if (estimatedTokens <= 0) return false;

    return this.strategy.shouldCompact({
      estimatedTokens,
      contextWindow,
      messageCount: this.messagesSinceCompaction,
      lastCompactionSeq: this.lastCompactionSeq,
      timeSinceLastCompaction:
        this.lastCompactionTime !== null ? Date.now() - this.lastCompactionTime : null,
    });
  }

  async compact(trigger: CompactionTrigger, focus?: string): Promise<SnapshotEvent | null> {
    if (this._isCompacting) return null;
    this._isCompacting = true;
    this._startFired = false;
    this._endFired = false;
    try {
      return await this._doCompact(trigger, focus);
    } finally {
      this._isCompacting = false;
      // Every in-band `_endCompaction()` sits on a return path, so a throw
      // anywhere between the start callback and one of them used to leave the
      // host suppressing forwarded events for the rest of the session's life —
      // silently, since serve.ts only logs a rejected `compact()`. Firing it
      // here too keeps the guarantee exactly-once rather than at-least-once.
      //
      // Nested so the ordering survives a throwing host. `onCompactionEnd`
      // reaches `sendEvent` and can throw on a dead transport; sequencing the
      // two as plain statements would then skip the settle, and the settle is
      // the only thing that releases the host's turn gate. A signal that can be
      // skipped holds every later turn forever — worse than the lost reply this
      // whole change exists to prevent.
      try {
        this._endCompaction();
      } catch (err) {
        // The host's end callback is already failing — serve.ts reaches
        // `sendEvent` there, so a dead transport is the expected case. Not
        // rethrown: that would replace whatever `_doCompact` rejected with, and
        // `lifecycle.compact` awaits this bare, so the caller would see the
        // symptom instead of the cause. Logged rather than dropped, because an
        // end callback failing for some other reason is exactly the kind of
        // invisible failure this whole change exists to stop.
        this.opts.onLog?.(
          `[compaction] onCompactionEnd threw while unwinding: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        this.opts.onCompactionSettled?.();
      }
    }
  }

  /**
   * Fire {@link CompactionOrchestratorOptions.onCompactionEnd} once per
   * compaction. The host stops suppressing forwarded events here, so a second
   * call would emit a stray idle status over the injection turn that follows
   * it on the success path.
   */
  private _endCompaction(): void {
    // Paired with the start callback, never fired alone: the driver reads that
    // open `_doCompact` precede it, so a throw there reaches the `finally`
    // having announced no compaction phase for the host to close.
    if (!this._startFired || this._endFired) return;
    this._endFired = true;
    this.opts.onCompactionEnd?.();
  }

  /**
   * One compaction attempt, plus at most one narrowed retry.
   *
   * @param retryOf Set only by the retry path. It suppresses a second
   *   `onCompactionStart` (the retry continues the same compaction phase),
   *   guarantees the retry can never itself retry, and carries the first
   *   attempt's context reading forward.
   */
  private async _doCompact(
    trigger: CompactionTrigger,
    focus?: string,
    retryOf?: { tokensBefore: number },
  ): Promise<SnapshotEvent | null> {
    const isRetry = retryOf !== undefined;
    // The retry inherits the first attempt's reading: by now the context also
    // holds the aborted compaction turn, so a fresh read would describe that
    // rather than the conversation whose size operators are asked to watch.
    const tokensBefore = retryOf?.tokensBefore ?? this.contextFill(this.driver.getTokenUsage());
    const driverSessionId = this.driver.runtimeSessionId ?? null;
    const driverModel = this.driver.getModel() ?? null;
    const driverProvider = this.driver.driverInfo?.id ?? null;
    const coversFromSeq = this.lastCompactionSeq ?? 0;
    // Phase 3: capture the registry signature once at the start of the call
    // so all attempt-event branches (failed/validation-failed/success) share
    // the same value.
    const capabilitySignatureAtCompaction = this.opts.getCurrentCapabilitySignature?.() ?? null;

    // Shared fields on every CompactionAttemptEvent emitted from this call.
    const attemptBase = {
      trigger,
      model: driverModel,
      provider: driverProvider,
      inputTokens: tokensBefore,
      coversFromSeq,
      coversToSeq: this.currentSeq,
      driverSessionIdAtCompaction: driverSessionId,
      modelAtCompaction: driverModel,
      capabilitySignatureAtCompaction,
    };

    if (!isRetry) {
      this._startFired = true;
      this.opts.onCompactionStart?.(trigger);
    }
    this.opts.onLog?.(
      `[compaction] ${isRetry ? "Retrying" : "Starting"} (trigger=${trigger}, tokens=${tokensBefore})`,
    );

    const prompt = buildCompactionPrompt({
      agentPrompt: this.opts.agentCompactionPrompt,
      skillDirectives: this.opts.skillDirectives,
      mcpDirectives: this.opts.mcpDirectives,
      focus,
      // A re-compaction's reply replaces the previous summary, so the model has
      // to see it — otherwise it answers with a delta that fails validation.
      previousSummary: coversFromSeq > 0 ? this._carriedSummary() : undefined,
    });

    const { summaryText, promptError, elapsedMs, timedOut } =
      await this._runCompactionPrompt(prompt);
    const attempt = { ...attemptBase, elapsedMs };

    // ── Failure path: prompt() threw ──────────────────────────────────────
    if (promptError !== null) {
      const errorCode = this._emitThrownFailure(attempt, promptError, timedOut);
      this._beginCooldown();
      if (this._shouldRetry(trigger, errorCode, focus, isRetry)) {
        return await this._doCompact(trigger, RETRY_FOCUS_DIRECTIVE, { tokensBefore });
      }
      this._endCompaction();
      return null;
    }

    // ── Validation path: prompt succeeded but output may be malformed ────
    // Use actual output token count from the driver; fall back to heuristic.
    const actualUsage = this.driver.getTokenUsage();
    const tokensAfter = actualUsage?.outputTokens ?? Math.ceil(summaryText.length / 4);

    const validation = validateCompactionOutput(summaryText, tokensBefore, tokensAfter);
    if (!validation.ok) {
      this._emitValidationFailure(attempt, validation.errorCode, tokensAfter, summaryText);
      this._beginCooldown();
      this._endCompaction();
      return null;
    }

    // ── Success path ──────────────────────────────────────────────────────
    return await this._completeCompaction(
      attempt,
      trigger,
      summaryText,
      tokensBefore,
      tokensAfter,
      coversFromSeq,
    );
  }

  /**
   * The previous summary as it goes into a re-compaction prompt, capped so the
   * prompt cannot grow without bound. Truncating loses context the model is
   * being asked to restate, so it is logged when it happens.
   */
  private _carriedSummary(): string | undefined {
    const summary = this.lastSummary;
    if (!summary) return undefined;
    if (summary.length <= MAX_CARRIED_SUMMARY_CHARS) return summary;
    this.opts.onLog?.(
      `[compaction] Previous summary truncated to ${MAX_CARRIED_SUMMARY_CHARS} of ${summary.length} chars`,
    );
    return truncateTo(summary, MAX_CARRIED_SUMMARY_CHARS);
  }

  /**
   * Start the anti-thrash cooldown after a failed attempt.
   *
   * `TokenThresholdStrategy` skips its cooldown gate entirely while
   * `lastCompactionTime` is null, and only success used to set it — so any
   * failing session re-ran a full-context compaction on every single turn.
   */
  private _beginCooldown(): void {
    this.lastCompactionTime = Date.now();
  }

  /**
   * Whether a failed attempt earns the one narrowed retry.
   *
   * Only automatic (threshold) compactions retry: a `manual` or `hibernate`
   * caller owns the decision to try again, and a caller-supplied `focus` would
   * be discarded by the retry's own directive.
   */
  private _shouldRetry(
    trigger: CompactionTrigger,
    errorCode: string,
    focus: string | undefined,
    isRetry: boolean,
  ): boolean {
    return (
      !isRetry &&
      trigger === "threshold" &&
      focus === undefined &&
      RETRYABLE_ERROR_CODES.has(errorCode)
    );
  }

  /**
   * Drive the compaction `prompt()` call, capturing summary text from the
   * driver's raw bridge-level events and any thrown error. The listener is
   * always removed in `finally`.
   *
   * `elapsedMs` is the wall-clock cost of the `prompt()` call. It is the only
   * thing that separates a genuinely empty model response from a turn that was
   * settled by something other than the model, which resolves in milliseconds.
   *
   * The call is bounded by {@link compactionTimeoutMs}; on expiry the turn is
   * aborted and `timedOut` is returned so the caller can record a classified
   * `timeout` rather than wait out the driver's much longer stall watchdog.
   */
  private async _runCompactionPrompt(prompt: string): Promise<{
    summaryText: string;
    promptError: unknown;
    elapsedMs: number;
    timedOut: boolean;
  }> {
    let summaryText = "";
    // The Claude SDK driver emits the SAME assistant text up to three times per
    // message: streamed `message_update` deltas, then `message_start` and
    // `message_end` both carrying the full block list. Capturing each one
    // tripled the stored summary. Mirrors EventNormalizer's `hasStreamedText`.
    let sawDeltas = false;
    const capturedMessages = new WeakSet<object>();
    // The driver emits raw bridge-level events (message_update, message_start,
    // message_end) that are NOT in the AgentEvent union — those are normalized
    // by EventNormalizer in serve.ts. Since the compaction handler attaches
    // directly to the driver, we type the parameter loosely.
    const captureHandler = (raw: unknown) => {
      const event = raw as { type: string; [k: string]: unknown };
      // Normalized events (from EventNormalizer or other drivers)
      if (event.type === "text") {
        summaryText += (event.content as string) ?? "";
        return;
      }
      // Raw SDK driver events — message_update carries _textDelta, bypassing
      // the EventNormalizer which isn't in the compaction capture path.
      if (event.type === "message_update") {
        const delta = event._textDelta as string | undefined;
        if (delta) {
          summaryText += delta;
          sawDeltas = true;
        }
        return;
      }
      // Fallback: non-streaming drivers may emit message_start/message_end
      // with the full text in the message content blocks.
      if (event.type === "message_start" || event.type === "message_end") {
        const msg = event.message as
          | { content?: { type: string; text?: string }[] }
          | undefined
          | null;
        if (!msg?.content) return;
        const alreadyCaptured = capturedMessages.has(msg);
        // Claim the message either way, so the paired event is skipped too.
        capturedMessages.add(msg);
        if (alreadyCaptured || sawDeltas) {
          if (event.type === "message_start") sawDeltas = false;
          return;
        }
        const blocks = Array.isArray(msg.content) ? msg.content : [];
        for (const b of blocks) {
          if (b.type === "text" && b.text) summaryText += b.text;
        }
      }
    };

    let promptError: unknown = null;
    let timedOut = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    const timeoutMs = compactionTimeoutMs();
    try {
      this.driver.on("agent-event", captureHandler);
      const running = this.driver.prompt(prompt);
      const expiry = new Promise<"expired">((resolve) => {
        timer = setTimeout(() => resolve("expired"), timeoutMs);
      });
      const outcome = await Promise.race([running.then(() => "settled" as const), expiry]);
      if (outcome === "expired") {
        timedOut = true;
        // Claim the abandoned turn's eventual rejection so it cannot crash the
        // runner, and keep the handle: it is the only signal that it ended.
        const settled = running.then(
          () => undefined,
          () => undefined,
        );
        promptError = new Error(
          `Compaction prompt did not complete within ${timeoutMs}ms; the turn was aborted.`,
        );
        // The abort is bounded too: ClaudeSdkDriver.abort() awaits
        // query.interrupt() with no deadline, and a wedged query is exactly
        // what got us here. A hung abort would strand `_isCompacting` and
        // suppress every agent event for the rest of the session.
        const aborted = this.driver.abort().catch((abortErr: unknown) => {
          this.opts.onLog?.(`[compaction] abort after timeout failed: ${String(abortErr)}`);
        });
        let settleTimer: ReturnType<typeof setTimeout> | undefined;
        const graceExpired = new Promise<void>((resolve) => {
          settleTimer = setTimeout(resolve, ABORT_SETTLE_GRACE_MS);
          settleTimer.unref?.();
        });
        // Proceed once the driver has both acknowledged the abort and unwound
        // the turn, or the grace runs out. Absorbed into elapsedMs.
        await Promise.race([Promise.all([aborted, settled]), graceExpired]);
        clearTimeout(settleTimer);
      }
    } catch (err) {
      promptError = err;
    } finally {
      clearTimeout(timer);
      this.driver.removeListener("agent-event", captureHandler);
    }

    return { summaryText, promptError, elapsedMs: Date.now() - startedAt, timedOut };
  }

  /**
   * Record a `failed` attempt for a thrown `prompt()` error: classify the
   * error, map it to an error code, log the raw text, and emit the attempt
   * event. Signalling compaction end is the caller's job — a retry continues
   * the same phase.
   *
   * @param timedOut Set when the bound in {@link _runCompactionPrompt} expired.
   *   The synthetic message carries none of the classifier's keywords, so
   *   round-tripping it would report the opaque `unknown` this bound replaces.
   * @returns The error code recorded, so the caller can decide about a retry.
   */
  private _emitThrownFailure(
    attemptBase: CompactionAttemptBase,
    promptError: unknown,
    timedOut = false,
  ): string {
    const rawMessage = promptError instanceof Error ? promptError.message : String(promptError);
    const classified = classifyClaudeSdkError(rawMessage);
    // Map classifier categories to our error codes per spec.
    // `auth` covers both real credential failures AND payment-blocked OAuth
    // subscriptions that Anthropic mislabels as auth errors (see
    // bridge/error-classifier.ts docstring) — owners investigate further.
    const classifiedCode =
      classified.category === "rate_limit"
        ? "rate_limit"
        : classified.category === "auth"
          ? "auth"
          : classified.category === "network"
            ? "timeout"
            : classified.category === "validation"
              ? "parse_error"
              : classified.category;
    const errorCode = timedOut ? "timeout" : classifiedCode;
    const diagnostic = truncateDiagnostic(classified.message);

    this.opts.onLog?.(`[compaction] Failed: ${classified.message} (${errorCode})`);
    this.opts.onCompactionAttempt?.({
      // Spread first: `status`/`validatedAt` below are load-bearing (see the
      // summary comment) and must not be override-able from the base object.
      ...attemptBase,
      type: "compaction_attempt",
      status: "failed",
      errorCode,
      errorMessage: diagnostic,
      // Mirrored so the platform's existing `summary` column explains the
      // failure with no schema change. Safe: a failed attempt is never
      // restored — restoration needs `success` plus a `validatedAt` stamp.
      summary: diagnostic,
      outputTokens: 0,
      validatedAt: null,
    });
    return errorCode;
  }

  /**
   * Record a `failed` attempt for output that passed `prompt()` but failed
   * structural validation. The rejected text is persisted so the next
   * occurrence is diagnosable from the row alone; signalling compaction end is
   * the caller's job.
   */
  private _emitValidationFailure(
    attemptBase: CompactionAttemptBase,
    errorCode: string,
    tokensAfter: number,
    rejectedText: string,
  ): void {
    this.opts.onLog?.(
      `[compaction] Output failed validation (${errorCode}); recording failed attempt`,
    );
    this.opts.onCompactionAttempt?.({
      // Spread first — see _emitThrownFailure.
      ...attemptBase,
      type: "compaction_attempt",
      status: "failed",
      errorCode,
      errorMessage: `Compaction output rejected by validation (${errorCode}): ${rejectedText.length} chars, ${tokensAfter} output tokens`,
      // The rejected text itself, not a summary — see _emitThrownFailure on
      // why persisting it here cannot leak into a restored session.
      summary: truncateDiagnostic(rejectedText),
      outputTokens: tokensAfter,
      validatedAt: null,
    });
  }

  /**
   * Success path: emit the attempt + snapshot events, update internal state,
   * and (unless hibernating) reset the driver and inject the snapshot.
   */
  private async _completeCompaction(
    attemptBase: CompactionAttemptBase,
    trigger: CompactionTrigger,
    summaryText: string,
    tokensBefore: number,
    tokensAfter: number,
    coversFromSeq: number,
  ): Promise<SnapshotEvent> {
    const validatedAt = new Date().toISOString();

    const snapshot: SnapshotEvent = {
      type: "snapshot",
      summary: summaryText,
      trigger,
      coversFromSeq,
      coversToSeq: this.currentSeq,
      tokensBefore,
      tokensAfter,
    };

    // 1. Emit the new attempt event AND the legacy snapshot event.
    //    Both ride alongside each other through Phase 4 (back-compat).
    this.opts.onCompactionAttempt?.({
      ...attemptBase,
      type: "compaction_attempt",
      status: "success",
      errorCode: null,
      summary: summaryText,
      outputTokens: tokensAfter,
      validatedAt,
    });
    this.opts.onSnapshot?.(snapshot);

    // 2. Update internal state
    this.lastCompactionSeq = this.currentSeq + 1;
    this.lastCompactionTime = Date.now();
    this.lastSummary = summaryText;
    this.messagesSinceCompaction = 0;

    // 3. Clear compacting flag so the injection turn's events flow normally
    this._endCompaction();

    // 4. Reset driver and inject snapshot (skip for hibernate -- container shuts down)
    if (trigger !== "hibernate") {
      this.opts.onLog?.("[compaction] Resetting driver session");
      await this.driver.resetSession();
      await this.driver.prompt(
        `<session_snapshot>\n${summaryText}\n</session_snapshot>\n\nThe conversation has been compacted. The above snapshot contains the full context of our work so far. Continue from where we left off.`,
      );
    }

    // 5. Log completion
    this.opts.onLog?.(
      `[compaction] Complete (${tokensBefore} -> ${tokensAfter} tokens, ratio=${((tokensAfter / Math.max(tokensBefore, 1)) * 100).toFixed(1)}%)`,
    );
    return snapshot;
  }
}
